import { API_BASE_URL } from '../../../lib/api';

import { apiFetch } from '@/lib/apiFetch';
import { withTenant } from '../../../lib/tenantHeaders';

const JSON_HEADERS = withTenant({ 'Content-Type': 'application/json' });

/**
 * CRUD de documentos requeridos por cliente, anidado bajo
 * /clients/:clienteId/documents. El backend valida que cada
 * documento pertenezca al cliente y que el tipo de documento
 * referenciado exista.
 */
export const fetchClientDocuments = async (clienteId) => {
  const res = await apiFetch(
    `${API_BASE_URL}/clients/${clienteId}/documents`,
    { headers: withTenant() },
  );
  return res.json();
};

export const createClientDocument = async (clienteId, data) => {
  const res = await apiFetch(
    `${API_BASE_URL}/clients/${clienteId}/documents`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(data),
    },
  );
  return res.json();
};

export const bulkCreateClientDocuments = async (clienteId, items) => {
  const res = await apiFetch(
    `${API_BASE_URL}/clients/${clienteId}/documents/bulk`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items }),
    },
  );
  return res.json();
};

export const updateClientDocument = async (clienteId, id, data) => {
  const res = await apiFetch(
    `${API_BASE_URL}/clients/${clienteId}/documents/${id}`,
    {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify(data),
    },
  );
  return res.json();
};

export const deleteClientDocument = async (clienteId, id) => {
  const res = await apiFetch(
    `${API_BASE_URL}/clients/${clienteId}/documents/${id}`,
    { method: 'DELETE', headers: withTenant() },
  );
  return res.json();
};

// ---------- Subida a R2 (presigned URL) ----------

export const getDocumentUploadUrl = async (clienteId, id, { fileName, mimeType, size }) => {
  const res = await apiFetch(
    `${API_BASE_URL}/clients/${clienteId}/documents/${id}/upload-url`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ fileName, mimeType, size }),
    },
  );
  return res.json();
};

export const confirmDocumentUpload = async (
  clienteId,
  id,
  { key, fileName, mimeType, size },
) => {
  const res = await apiFetch(
    `${API_BASE_URL}/clients/${clienteId}/documents/${id}/upload-complete`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ key, fileName, mimeType, size }),
    },
  );
  return res.json();
};

export const getDocumentDownloadUrl = async (clienteId, id) => {
  const res = await apiFetch(
    `${API_BASE_URL}/clients/${clienteId}/documents/${id}/download-url`,
    { headers: withTenant() },
  );
  return res.json();
};

/**
 * Sube un archivo a R2 usando el flujo de presigned URL.
 * 1. Pide al backend una URL firmada (PUT).
 * 2. Sube el archivo directo a R2 con `fetch` PUT.
 * 3. Confirma al backend para que persista los metadatos.
 *
 * Devuelve la respuesta del confirm (con el documento actualizado).
 */
export const uploadDocumentToR2 = async (
  clienteId,
  id,
  file,
  { onProgress } = {},
) => {
  // 1) Pedir URL firmada
  const urlRes = await getDocumentUploadUrl(clienteId, id, {
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
  });
  if (!urlRes?.success) {
    throw new Error(urlRes?.message || 'No se pudo obtener la URL de subida');
  }
  const { uploadUrl, key } = urlRes.data;

  // 2) PUT directo a R2. Algunos browsers no soportan `onuploadprogress`
  //    en fetch, asÃ­ que el callback puede no dispararse.
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`R2 respondiÃ³ ${xhr.status}: ${xhr.responseText}`));
    };
    xhr.onerror = () => reject(new Error('Error de red subiendo a R2'));
    xhr.send(file);
  });

  // 3) Confirmar al backend
  const confirmRes = await confirmDocumentUpload(clienteId, id, {
    key,
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
  });
  return confirmRes;
};
